<template>
  <div
    :class="[
      'template-1',
      { 'simple-preview-mode': processNum === CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM },
      { 'full-screen-mode': isFullScreenPreviewMode },
      { 'content-list-preview': processNum === CREATE_CONTENT_PROCESS_NUM.CONTENT_LIST_PREVIEW },
    ]"
  >
    <!-- 全画面プレビュー表示 -->
    <div v-if="isFullScreenPreviewMode" class="full-preview-content" ref="fullPreviewContent">
      <template v-if="contentMaterialList.length">
        <div class="full-preview-material1" ref="fullPreviewMaterial1">
          <img
            v-if="isImageFile(contentMaterialList[0].materialUrl)"
            class="material-img1"
            :src="getImageUrl(0, contentMaterialList, timestamp)"
            alt="material image1"
            @load="reallocateMaterial1"
            :style="materialStyle1"
          />
          <video
            v-else
            class="material-img1"
            autoplay
            playsinline
            muted
            loop
            :src="`${AZURE_CDN.ENDPOINT}${contentMaterialList[0].materialUrl}${AZURE_CDN.SAS}`"
            @loadeddata="reallocateMaterial1"
            :style="materialStyle1"
          />
        </div>
      </template>
      <div class="caption" ref="fullscreenCaptionRef" :style="[captionColor]">
        <template-preview-caption-text
          :fontSize="fullscreenCaptionFontSize"
          :contentCaption="contentCaption"
          :contentCaptionShadowColor="contentCaptionShadowColor"
        />
      </div>
    </div>
    <!-- 簡易プレビュー表示 -->
    <div
      v-else-if="
        [
          CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM,
          CREATE_CONTENT_PROCESS_NUM.CONTENT_LIST_PREVIEW,
        ].includes(processNum)
      "
      class="simple-preview-content"
    >
      <div class="simple-preview-material1" ref="simplePreviewMaterial1">
        <img
          v-if="contentMaterialList[0]"
          :src="materialUrl(contentMaterialList[0].materialUrl)"
          alt="material image1"
          class="material-img1"
          @load="reallocateMaterial1"
          :style="materialStyle1"
        />
        <button class="edit-button" @click="clickMaterial(0)" v-show="isEditing">
          <img :src="EDIT_ICON" alt="icon edit image1" class="edit-icon" />
        </button>
        <audio-button
          :materialNum="0"
          :isEditing="isEditing"
          :contentMaterialList="contentMaterialList"
          :isAudioButtonVisible="isAudioButtonVisible"
        />
      </div>
      <div class="caption" :style="[captionColor]">
        <template-preview-caption-text
          :contentCaption="contentCaption"
          :contentCaptionShadowColor="contentCaptionShadowColor"
        />
      </div>
    </div>
    <!-- フッターでのプレビュー表示 -->
    <div
      v-else
      :class="[
        'footer-preview-wrapper',
        `process-${processNum}`,
        {
          'border-solid':
            processNum === CREATE_CONTENT_PROCESS_NUM.MATERIAL_SELECT &&
            contentMaterialList[0] &&
            contentMaterialList[0].materialUrl,
        },
      ]"
    >
      <img
        v-if="contentMaterialList[0] && contentMaterialList[0].materialUrl"
        :src="materialUrl(contentMaterialList[0].materialUrl)"
        alt="material image"
        class="material-img"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted, computed, onBeforeUnmount } from 'vue';
import {
  isImageFile,
  getImageUrl,
  getThumbnailUrl,
  reallocateMaterial,
  allocateMaterial,
} from '@/composable/material_common';
import AudioButton from '@/components/common/AudioButton.vue';
import TemplatePreviewCaptionText from '@/components/common/TemplatePreviewCaptionText.vue';
import {
  CREATE_CONTENT_PROCESS_NUM,
  CONTENT_CAPTION_COLOR,
  EDIT_ICON,
} from '@/composable/constant_common';
import { AZURE_CDN } from '@/composable/constant';

const props = defineProps({
  isFullScreenPreviewMode: Boolean,
  isVisible: Boolean,
  processNum: Number,
  contentMaterialList: Array,
  clickMaterial: Function,
  contentCaption: String,
  contentCaptionColor: String,
  isEditing: Boolean,
  isAudioButtonVisible: Boolean,
  timestamp: {
    type: Number,
    default: 0,
  },
});

const fullscreenCaptionRef = ref(null);
const fullscreenCaptionFontSize = ref(20);

// 素材1の情報
const materialInformation1 = ref({
  displayAreaWidth: 0,
  displayAreaHeight: 0,
  materialWidth: 0,
  materialHeight: 0,
  offsetX: 0,
  offsetY: 0,
  scale: 1,
});

const fullPreviewContent = ref(null);
const simplePreviewMaterial1 = ref(null);
const fullPreviewMaterial1 = ref(null);

const materialUrl = (url) => {
  return `${AZURE_CDN.ENDPOINT}${getThumbnailUrl(encodeURI(url))}${AZURE_CDN.SAS}`;
};

const updateFullscreenCaptionFontSize = () => {
  if (!props.isFullScreenPreviewMode) return;
  nextTick(() => {
    const fullArea = fullscreenCaptionRef.value;
    if (!fullArea) {
      return;
    }
    const areaHeightPx = fullArea.offsetHeight;
    if (areaHeightPx > 0) {
      fullscreenCaptionFontSize.value = areaHeightPx * 0.85;
    }
  });
};

const reallocateMaterial1 = (event) => {
  if (props.isEditing) {
    materialInformation1.value = reallocateMaterial(
      0,
      props.contentMaterialList,
      props.isFullScreenPreviewMode,
      event
    );
  } else {
    materialInformation1.value = allocateMaterial(
      props.contentMaterialList[0],
      props.isFullScreenPreviewMode,
      event
    );
  }
};

const getSimplePreviewAreaSize = () => {
  const element1 = simplePreviewMaterial1.value;
  if (element1) {
    materialInformation1.value.displayAreaWidth = element1.getBoundingClientRect().width;
    materialInformation1.value.displayAreaHeight = element1.getBoundingClientRect().height;
  }
};

const getFullPreviewAreaSize = () => {
  const element1 = fullPreviewMaterial1.value;
  if (!element1) return;
  materialInformation1.value.displayAreaWidth = element1.getBoundingClientRect().width;
  materialInformation1.value.displayAreaHeight = element1.getBoundingClientRect().height;
};

const materialStyle1 = computed(() => {
  return {
    '--material1-offset-x': `${
      materialInformation1.value.offsetX * materialInformation1.value.displayAreaWidth
    }px`,
    '--material1-offset-y': `${
      materialInformation1.value.offsetY * materialInformation1.value.displayAreaHeight
    }px`,
    '--material1-scale':
      (materialInformation1.value.scale * materialInformation1.value.displayAreaWidth) /
      materialInformation1.value.materialWidth,
  };
});

const captionColor = computed(() => {
  return {
    '--caption-color': props.contentCaptionColor,
  };
});

const contentCaptionShadowColor = computed(() => {
  return props.contentCaptionColor === CONTENT_CAPTION_COLOR.white
    ? CONTENT_CAPTION_COLOR.black
    : CONTENT_CAPTION_COLOR.white;
});

// 字幕テキスト・全画面フラグ変化時に実行
watch([() => props.isVisible], () => updateFullscreenCaptionFontSize());

onMounted(() => {
  if (props.processNum === CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM) {
    if (props.isFullScreenPreviewMode) {
      const observer = new IntersectionObserver(getFullPreviewAreaSize);
      observer.observe(fullPreviewContent.value);
      window.addEventListener('resize', getFullPreviewAreaSize);
    } else {
      getSimplePreviewAreaSize();
      window.addEventListener('resize', getSimplePreviewAreaSize);
    }
  }
  updateFullscreenCaptionFontSize();

  // レンダリング安定させてから再計算
  setTimeout(updateFullscreenCaptionFontSize, 100);

  window.addEventListener('resize', updateFullscreenCaptionFontSize);
});

onBeforeUnmount(() => {
  if (props.processNum === CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM) {
    if (props.isFullScreenPreviewMode) {
      window.removeEventListener('resize', getFullPreviewAreaSize);
    } else {
      window.removeEventListener('resize', getSimplePreviewAreaSize);
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateFullscreenCaptionFontSize);
});
</script>

<style scoped>
.full-preview-mode,
.simple-preview-mode,
.full-preview-content,
.simple-preview-content {
  width: 100%;
  height: 100%;
}
.full-preview-content,
.simple-preview-content {
  position: relative;
  background-color: black;
}
.full-preview-material1,
.simple-preview-material1 {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}
.material-img1 {
  transform-origin: left top;
  transform: translate(var(--material1-offset-x), var(--material1-offset-y))
    scale(var(--material1-scale));
}
.footer-preview-wrapper {
  position: relative;
  aspect-ratio: 9 / 16;
  height: 10rem;
  max-width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  box-sizing: border-box;
  isolation: isolate;
}
.content-list-preview,
.content-list-preview * {
  border-radius: 0 !important;
}
.content-list-preview {
  height: 100%;
}
.material-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.process-2 .material-img {
  opacity: 0.3;
}
.full-preview-content .caption p {
  font-weight: bold;
  margin: 0;
  padding-left: 100%;
  transform: translateX(0);
}
.process-4.footer-preview-wrapper {
  height: 100%;
}
.process-1.footer-preview-wrapper {
  border: 0.2rem #21963b dashed;
  position: relative;
  z-index: 1;
}
.caption {
  bottom: 0;
  color: var(--caption-color);
  margin: 0;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 100%;
  box-sizing: border-box;
  border-radius: 0.5rem;
  transform: translate3d(0, 0, 0);
  -webkit-transform: translate3d(0, 0, 0);
  text-align: left;
}
.process-2 .caption {
  color: #21963b;
  border: 0.2rem #21963b dashed;
}
.caption p {
  font-size: 1.7rem;
  margin: 0;
  padding-left: 10%;
}
.process-1.footer-preview-wrapper::after {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: 2rem;
  width: 2rem;
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  border-radius: 50%;
  content: '1';
  color: white;
  background-color: #21963b;
}
.process-1.border-solid,
.process-2 .border-solid {
  border: 0.2rem #21963b solid;
}
.simple-preview-mode .caption p {
  font-size: 3rem;
}
.edit-button {
  position: absolute;
  top: 7px;
  right: 8px;
  width: 40px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 10%;
}
.edit-icon {
  width: 40px;
  height: 30px;
  border-radius: 10%;
  background-color: #ffffff;
  overflow: hidden;
}
.edit-icon:hover {
  opacity: 0.8;
}

.full-screen-mode .caption {
  height: 13%;
  bottom: 0;
  align-items: flex-end;
}

.full-screen-mode .caption p {
  width: fit-content;
  height: 100%;
}
</style>
